import { Box, Typography } from "@material-ui/core"
import IntlTelInput from "intl-tel-input/reactWithUtils"
import "intl-tel-input/styles"
import { makeStyles } from "@material-ui/core"

import { colors } from "../../../utils/theme"

interface PhoneInputProps {
  isDisabled: boolean
  phoneErrorMessage: string | null
  label: string
  onChangeValidity: (valid: boolean) => void
  onChangeNumber: (num: string) => void
}

/**
 * Phone number input built on `intl-tel-input`.
 *
 * - `onChangeNumber` emits the formatted E.164-ish number provided by `intl-tel-input`.
 * - `onChangeValidity` emits whether the current number is valid.
 *
 * Error handling is owned by the parent:
 * - Pass `phoneErrorMessage` to display an error below the field.
 * - When an error exists, the input is marked with `aria-invalid` and linked to the
 *   helper text via `aria-describedby` for accessibility.
 */
export default function PhoneInput({
  isDisabled,
  phoneErrorMessage,
  label,
  onChangeValidity,
  onChangeNumber,
}: PhoneInputProps) {
  const classes = useStyles()

  return (
    <Box className={classes.phoneInputContainer}>
      <IntlTelInput
        onChangeNumber={onChangeNumber}
        onChangeValidity={onChangeValidity}
        disabled={isDisabled}
        usePreciseValidation
        initOptions={{
          initialCountry: "us",
          nationalMode: false,
          formatOnDisplay: true,
          useFullscreenPopup: false,
          fixDropdownWidth: true,
        }}
        inputProps={{
          className: classes.phoneInput,
          placeholder: label,
          "aria-label": label,
          "aria-invalid": Boolean(phoneErrorMessage),
          "aria-describedby": phoneErrorMessage
            ? "invite-client-phone-error"
            : undefined,
        }}
      />
      {phoneErrorMessage && (
        <Typography id="invite-client-phone-error" color="error" role="alert">
          {phoneErrorMessage}
        </Typography>
      )}
    </Box>
  )
}

const useStyles = makeStyles((theme) => ({
  phoneInputContainer: {
    width: "100%",
    "& .iti": {
      width: "100%",
    },
  },
  phoneInput: {
    // Do my best to mimic our input styles
    width: "100%",
    boxSizing: "border-box",
    height: theme.spacing(7),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${colors.neutral.clay}`,
    color: colors.neutral.denim,
    backgroundColor: colors.neutral.white,
    outline: "none",
    "&:focus": {
      borderColor: colors.neutral.denim,
      borderWidth: "2px",
    },
    "&[disabled]": {
      opacity: 0.5,
      cursor: "not-allowed",
      borderColor: colors.neutral.clay,
      backgroundColor: colors.neutral.white,
      color: colors.neutral.denim,
    },
    "&:hover": {
      borderColor: colors.primary.periwinkle,
      backgroundColor: colors.primary.cloud,
    },
    "&::placeholder": {
      color: colors.neutral.denim,
    },
  },
}))
